import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { Box, useMediaQuery, useTheme } from '@mui/material';

import { ColorInputPicker } from '~/components/atoms/ColorInputPicker';
import AsyncSwitch from '~/components/ui/AsyncSwitch';
import Checkbox from '~/components/ui/Checkbox';
import Select from '~/components/ui/Select';
import { Text } from '~/components/ui/text';
import TextInput from '~/components/ui/TextInput';
import { BASIC_GRADIENT_STYLES, DESKTOP_MIN_WIDTH, FONTS, GRADIENT_TYPES } from '~/constants/common';
import useDesignQRSectionSectionStore from '~/store/useDesignQRSectionStore';
import { COLORS } from '~/styles/colors';
import { QrCodeStyle } from '~/types/qrCode';
import { isMaxLength } from '~/utils/common';

type QRFrameTextFormProps = {
  qrStyle: QrCodeStyle;
  onUpdateStyles: (newStyles: QrCodeStyle) => void;
};

export default function QRFrameTextForm({ qrStyle, onUpdateStyles }: QRFrameTextFormProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));

  const { selectedFrame } = useDesignQRSectionSectionStore();
  const [textValue, setTextValue] = useState(qrStyle?.frame?.phrase);
  const [isGradientFrameColor, setIsGradientFrameColor] = useState(typeof qrStyle?.frame?.color === 'object');
  const [isGradientFrameBgColor, setIsGradientFrameBgColor] = useState(
    typeof qrStyle?.shape?.backgroundColor === 'object',
  );

  const styles = useStyles({ isDesktop });

  const qrCodeDataStylesRef = useRef(qrStyle);

  const handleUpdateTextColor = (field: string, value: string | boolean, styleField: string) => {
    if (field === 'phrase' && typeof value === 'string') {
      setTextValue(value);
    }

    if (qrStyle) {
      const newStyle = {
        ...qrCodeDataStylesRef.current,
        [styleField]: { ...qrCodeDataStylesRef.current?.[styleField], [field]: value },
      };

      qrCodeDataStylesRef.current = newStyle;

      onUpdateStyles(newStyle);
    }
  };

  const handleChangeGradient = ({
    field,
    styleField,
    value,
    colorField,
  }: {
    field: string;
    value: string;
    styleField: string;
    colorField: string;
  }) => {
    const isGradient = typeof qrCodeDataStylesRef.current?.[styleField]?.[colorField] === 'object';
    const gradientStyles = isGradient ? qrCodeDataStylesRef.current?.[styleField]?.[colorField] : BASIC_GRADIENT_STYLES;
    const newStyle = {
      ...qrCodeDataStylesRef.current,
      [styleField]: {
        ...qrCodeDataStylesRef.current?.[styleField],
        [colorField]: { ...gradientStyles, [field]: value },
      },
    };

    qrCodeDataStylesRef.current = newStyle;
    onUpdateStyles(newStyle);
  };

  const handleUpdateTextFont = (textFont: string) => {
    if (qrStyle) {
      const newStyle = {
        ...qrCodeDataStylesRef.current,
        frame: {
          ...qrCodeDataStylesRef.current?.frame,
          font_style: textFont,
        },
      };

      qrCodeDataStylesRef.current = newStyle;

      onUpdateStyles(newStyle);
    }
  };

  useEffect(() => {
    if (qrStyle) {
      qrCodeDataStylesRef.current = qrStyle;
    }
  }, [qrStyle]);

  return (
    <>
      <Box sx={styles.firstBlock}>
        <TextInput
          onChange={(e) => handleUpdateTextColor('phrase', e?.target?.value, 'frame')}
          defaultValue={qrStyle?.frame?.phrase}
          label={t('builder:designStep.frame.frameText')}
          fullWidth
          inputProps={{ maxLength: 20 }}
          helperText={isMaxLength(textValue, 20)}
          absoluteHelperText
        />
        <Select
          items={FONTS}
          onChange={(e) => handleUpdateTextFont(e.target.value)}
          fullWidth
          label={t('builder:designStep.frame.fontStyle')}
          defaultValue={FONTS[3].value}
        />
        <ColorInputPicker
          fullWidth
          key={selectedFrame?.defaultTextColor}
          label={t('labels:textColor')}
          onChange={(value) => handleUpdateTextColor('phrase_color', value, 'frame')}
          defaultValue={selectedFrame?.defaultTextColor}
          absoluteHelperText
        />
      </Box>

      <Box sx={styles.blockItem}>
        <Text variant="subheader">{t('thirdStep:frameColour')}</Text>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <AsyncSwitch
            label={t('thirdStep:useGradients')}
            checked={isGradientFrameColor}
            sx={{ width: 'fit-content' }}
            onChange={(e) => {
              const { checked } = e.target;

              if (checked) {
                handleChangeGradient({ field: 'color', value: '#000000', styleField: 'frame', colorField: 'color' });
              } else {
                handleUpdateTextColor('color', '#47787d', 'frame');
              }

              setIsGradientFrameColor(checked);
            }}
          />
          {isGradientFrameColor ? (
            <Box sx={{ display: 'flex', marginTop: '12px', gap: '16px', flexDirection: isDesktop ? 'row' : 'column' }}>
              <Select
                items={GRADIENT_TYPES}
                fullWidth
                sx={{ flex: 1 }}
                label={t('thirdStep:gradientType')}
                defaultValue={qrStyle?.frame?.color?.type || GRADIENT_TYPES[0]?.value}
                onChange={(e) =>
                  handleChangeGradient({
                    field: 'type',
                    value: e.target.value,
                    styleField: 'frame',
                    colorField: 'color',
                  })
                }
              />

              <ColorInputPicker
                label={t('common:color')}
                onChange={(val) =>
                  handleChangeGradient({ field: 'start_color', value: val, styleField: 'frame', colorField: 'color' })
                }
                sx={{ flex: 1 }}
                fullWidth
                defaultValue={qrStyle?.frame?.color?.startColor}
              />

              <ColorInputPicker
                label={t('common:color')}
                onChange={(val) =>
                  handleChangeGradient({ field: 'end_color', value: val, styleField: 'frame', colorField: 'color' })
                }
                sx={{ flex: 1 }}
                fullWidth
                defaultValue={qrStyle?.frame?.color?.endColor ?? '#FFFFFF'}
              />
            </Box>
          ) : (
            <ColorInputPicker
              onChange={(value) => handleUpdateTextColor('color', value, 'frame')}
              sx={{ mt: 2, width: isDesktop ? 'calc(50% - 8px)' : '100%' }}
              defaultValue={typeof qrStyle?.frame?.color !== 'object' ? qrStyle?.frame?.color : ''}
            />
          )}
        </Box>
      </Box>

      <Box sx={styles.blockItem}>
        <Text variant="subheader">{t('builder:designStep.frame.frameBg')}</Text>
        <Checkbox
          label={t('thirdStep:transparentBg')}
          defaultChecked={qrStyle?.shape?.transparentBg}
          onChange={(e) => handleUpdateTextColor('transparentBg', e.target.checked, 'shape')}
        />
        <AsyncSwitch
          label={t('thirdStep:useGradients')}
          checked={isGradientFrameBgColor}
          sx={{ width: 'fit-content' }}
          onChange={(e) => {
            const { checked } = e.target;

            if (checked) {
              handleChangeGradient({
                field: 'backgroundColor',
                value: '#000000',
                styleField: 'shape',
                colorField: 'backgroundColor',
              });
            } else {
              handleUpdateTextColor('backgroundColor', '#47787d', 'shape');
            }

            setIsGradientFrameBgColor(checked);
          }}
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: isDesktop ? 'center' : 'initial',
            flexDirection: isDesktop ? 'row' : 'column',
            gap: '16px',
          }}
        >
          {isGradientFrameBgColor ? (
            <>
              <Select
                items={GRADIENT_TYPES}
                fullWidth
                sx={{ flex: 1 }}
                label={t('thirdStep:gradientType')}
                defaultValue={qrStyle?.shape?.backgroundColor?.type || GRADIENT_TYPES[0]?.value}
                onChange={(e) =>
                  handleChangeGradient({
                    field: 'type',
                    value: e.target.value,
                    styleField: 'shape',
                    colorField: 'backgroundColor',
                  })
                }
              />

              <ColorInputPicker
                label={t('common:color')}
                onChange={(val) =>
                  handleChangeGradient({
                    field: 'start_color',
                    value: val,
                    styleField: 'shape',
                    colorField: 'backgroundColor',
                  })
                }
                sx={{ flex: 1 }}
                fullWidth
                defaultValue={qrStyle?.shape?.backgroundColor?.startColor}
              />

              <ColorInputPicker
                label={t('common:color')}
                onChange={(val) =>
                  handleChangeGradient({
                    field: 'end_color',
                    value: val,
                    styleField: 'shape',
                    colorField: 'backgroundColor',
                  })
                }
                sx={{ flex: 1 }}
                fullWidth
                defaultValue={qrStyle?.shape?.backgroundColor?.endColor ?? '#FFFFFF'}
              />
            </>
          ) : (
            <ColorInputPicker
              sx={{ width: isDesktop ? 'calc(50% - 8px)' : '100%' }}
              onChange={(value) => handleUpdateTextColor('backgroundColor', value, 'shape')}
              defaultValue={typeof qrStyle?.shape?.backgroundColor === 'string' ? qrStyle.shape.backgroundColor : ''}
            />
          )}
        </Box>
      </Box>
    </>
  );
}

function useStyles({ isDesktop }) {
  return {
    firstBlock: css`
      gap: 16px;
      width: 100%;
      display: flex;
      margin-top: 20px;
      align-items: ${isDesktop ? 'flex-end' : 'flex-start'};
      flex-direction: ${isDesktop ? 'row' : 'column'};
    `,
    blockItem: css`
      gap: 16px;
      border-radius: 16px;
      width: 100%;
      display: flex;
      background-color: ${COLORS.lightGray};
      padding: 16px;
      flex-direction: column;
      margin-top: 20px;
      border: 1px solid ${COLORS.grey200};
    `,
  };
}
